<script setup>
import {
  ref,
  defineModel,
  defineProps,
  defineEmits,
  reactive,
  computed,
} from "vue";
import UpdatesBehaviour from "./components/UpdatesBehaviour.vue";
import NotificationSettings from "./components/NotificationSettings.vue";
import AppsList from "./components/AppsList.vue";
import { apiQuery } from "@/api/j24api";
import AlertsList from "@/components/AlertsList.vue";
import AppRules from "./components/AppRules.vue";
import validateConfiguration from "./components/validateConfiguration";
import { storeToRefs } from "pinia";
import { patch24Store } from "@/stores/patch24";
const patch24 = patch24Store();

const model = defineModel();
const selectedConfig = ref(null);
const emit = defineEmits(["saved"]);
const props = defineProps({
  globalConfig: Object,
  apps: Array,
  licenseId: String,
  readOnlyMode: Boolean,
});
const selectedAppsTab = ref("add");
const alerts = ref([]);
const selectedConfigToDelete = ref(null);
const showConfirmDelete = ref(false);
const isDeleting = ref(false);
const devicesUsingConfig = ref(0);
const checkingDevices = ref(false);

const headers = computed(() => {
  const baseHeaders = [
    { title: "Name", value: "ConfigurationName" },
    { title: "Config ID", value: "ConfigurationID" },
  ];
  if (!props.readOnlyMode) {
    baseHeaders.push({ title: "Actions", value: "actions" });
  }
  return baseHeaders;
});

function rowClick(item, row) {
  console.log(item);
  console.log(row.item);
  //selectedConfig = {};
  selectedConfig.value = { ...row.item };
  if (!selectedConfig.value.AppsToAdd) {
    selectedConfig.value.AppsToAdd = [];
  }
  if (!selectedConfig.value.AppsToExclude) {
    selectedConfig.value.AppsToExclude = [];
  }
  if (!selectedConfig.value.inheritApps) {
    selectedConfig.value.inheritApps = false;
  }
  if (!selectedConfig.value.AppsSelected) {
    selectedConfig.value.AppsSelected = [];
  }
  // if selectedConfig.inheritAppRules is not set, set it to true
  if (selectedConfig.value.inheritAppRules === undefined) {
    selectedConfig.value.inheritAppRules = true;
  }
  if (!selectedConfig.value.AppRules) {
    selectedConfig.value.AppRules = [];
  }
  console.log("Selected config", selectedConfig.value);
}

const isSaving = ref(false);
const isAdding = ref(false);

async function getPatchDevices() {
  try {
    let result = await apiQuery("ELA", "patching/patchingManagement", {
      action: "getDevices",
      id: props.licenseId,
      minimal: true,
    });
    if (result.length) {
      return result;
    }
  } catch (error) {
    alerts.value.push({
      type: "error",
      message: "Error getting devices",
      closable: true,
    });
  }
}

async function save() {
  isSaving.value = true;
  try {
    let validationErrors = validateConfiguration(selectedConfig.value);

    if (validationErrors.errors.length) {
      alerts.value.push(...validationErrors.errors);
      return;
    }
    var result = await apiQuery("ELA", "patching/patchingManagement", {
      action: "saveGroupConfig",
      configuration: selectedConfig.value,
      id: props.licenseId,
      test: true,
    });
    if (result) {
      alerts.value.push({
        type: "success",
        message: "Configuration saved successfully",
        closable: true,
      });
      emit("saved");
      selectedConfig.value = null;
    } else {
      alerts.value.push({
        type: "error",
        message: "Error saving configuration",
        closable: true,
      });
    }
  } catch (error) {
    alerts.value.push({
      type: "error",
      message: "Error saving configuration - exception",
      closable: true,
    });
  } finally {
    isSaving.value = false;
  }
}

const newConfig = reactive({
  ConfigurationName: "",
});
const showNewConfigDialog = ref(false);

async function addNewConfig() {
  isAdding.value = true;
  try {
    const result = await apiQuery("ELA", "patching/patchingManagement", {
      action: "addGroupConfig",
      configuration: newConfig,
      id: props.licenseId,
    });
    if (result && result.Configurations) {
      alerts.value.push({
        type: "success",
        message: "New configuration added successfully",
        closable: true,
      });
      //model.push({ ...newConfig, ConfigurationID: result.ConfigurationID });
      if (result.Configurations?.length) {
        console.log("Adding to model", result.Configurations);
        model.value.length = 0;
        model.value.splice(0, 0, ...result.Configurations);
      }
      showNewConfigDialog.value = false;
    } else {
      alerts.value.push({
        type: "error",
        message: "Error adding new configuration",
        closable: true,
      });
    }
  } catch (error) {
    console.log(error);
    // If error contains Configuration name already exists
    if (error.message?.includes("Configuration name already exists")) {
      alerts.value.push({
        type: "error",
        message: "Configuration name already exists",
        closable: true,
      });
      return;
    }

    alerts.value.push({
      type: "error",
      message: "Error adding new configuration - exception",
      closable: true,
    });
  } finally {
    isAdding.value = false;
  }
}

async function deleteConfiguration(item) {
  selectedConfigToDelete.value = item;
  showConfirmDelete.value = true;
  checkingDevices.value = true;
  devicesUsingConfig.value = 0;

  try {
    const result = await apiQuery("ELA", "patching/patchingManagement", {
      action: "getDevices",
      id: props.licenseId,
      minimal: true,
    });

    if (result?.length) {
      const count = result.filter(
        (device) => device.configurationId === item.ConfigurationID
      ).length;
      devicesUsingConfig.value = count;
    }
  } catch (error) {
    console.error("Error checking devices:", error);
  } finally {
    checkingDevices.value = false;
  }
}

async function deleteConfigurationConfirmed() {
  isDeleting.value = true;
  try {
    const result = await apiQuery("ELA", "patching/patchingManagement", {
      action: "deleteGroupConfig",
      configurationId: selectedConfigToDelete.value.ConfigurationID,
      id: props.licenseId,
    });
    if (result.message === "Configuration deleted successfully") {
      alerts.value.push({
        type: "success",
        message: "Configuration deleted successfully",
        closable: true,
      });
      emit("saved");
    } else {
      alerts.value.push({
        type: "error",
        message: "Error deleting configuration",
        closable: true,
      });
    }
  } catch (error) {
    console.error(error);
    alerts.value.push({
      type: "error",
      message: "Error deleting configuration - exception",
      closable: true,
    });
  } finally {
    isDeleting.value = false;
    showConfirmDelete.value = false;
    selectedConfigToDelete.value = null;
  }
}
</script>

<template>
  <v-dialog v-model="showNewConfigDialog" max-width="500px">
    <v-card>
      <v-card-title>Add New Configuration Group</v-card-title>
      <v-card-text>
        <v-form>
          <v-text-field
            label="Configuration Name"
            v-model="newConfig.ConfigurationName"
            required
          ></v-text-field>
        </v-form>
      </v-card-text>
      <v-card-actions>
        <v-btn :loading="isAdding" color="primary" @click="addNewConfig">
          Submit
        </v-btn>
        <v-btn @click="showNewConfigDialog = false">Cancel</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <v-dialog v-model="showConfirmDelete" max-width="400">
    <v-card>
      <v-card-title class="headline">Delete Configuration?</v-card-title>
      <v-card-text>
        <div class="mb-2">
          Are you sure you want to delete this configuration?
        </div>
        <div v-if="selectedConfigToDelete">
          <div>
            <strong>Name:</strong>
            {{ selectedConfigToDelete.ConfigurationName }}
          </div>
          <div>
            <strong>Config ID:</strong>
            {{ selectedConfigToDelete.ConfigurationID }}
          </div>
        </div>
        <v-alert v-if="checkingDevices" type="info" class="mt-3">
          Checking device usage...
        </v-alert>
        <v-alert v-else-if="devicesUsingConfig > 0" type="warning" class="mt-3">
          <strong>Warning:</strong> {{ devicesUsingConfig }} device{{
            devicesUsingConfig > 1 ? "s are" : " is"
          }}
          currently using this configuration.
        </v-alert>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn @click="showConfirmDelete = false" variant="elevated">
          Cancel
        </v-btn>
        <v-btn
          color="primary"
          @click="deleteConfigurationConfirmed"
          variant="elevated"
          :loading="isDeleting || checkingDevices"
        >
          Delete
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <AlertsList v-model="alerts" />
  <template v-if="!selectedConfig">
    <v-row class="mb-3">
      <v-col cols="5" class="ml-1">
        <h2>Configuration Groups</h2>
      </v-col>
      <v-spacer></v-spacer>
      <v-col cols="auto" class="mr-1" v-if="!props.readOnlyMode">
        <v-btn color="primary" @click="showNewConfigDialog = true">
          Add New Configuration
        </v-btn>
      </v-col>
    </v-row>
    <v-sheet rounded="lg" class="ma-1" elevation="1">
      <v-data-table @click:row="rowClick" :headers="headers" :items="model">
        <template v-slot:[`item.actions`]="{ item }">
          <v-icon
            class="pointer"
            @click.stop="deleteConfiguration(item)"
            color="primary"
          >
            mdi-delete
          </v-icon>
        </template>
      </v-data-table>
    </v-sheet>
  </template>
  <template v-else>
    <v-row>
      <v-col cols="6">
        <h2>
          Editing Configuration Group: {{ selectedConfig.ConfigurationName }}
        </h2>
        <h4>Configuration ID: {{ selectedConfig.ConfigurationID }}</h4>
      </v-col>
      <v-spacer></v-spacer>
      <v-col cols="auto" v-if="!props.readOnlyMode">
        <v-btn :loading="isSaving" color="primary" @click="save()">Save</v-btn>
      </v-col>
      <v-col cols="auto">
        <v-btn @click="selectedConfig = nil">Cancel</v-btn>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="6">
        <v-sheet rounded="lg" elevation="2" height="100%" class="pa-5">
          <UpdatesBehaviour
            :globalConfig="props.globalConfig"
            :subConfig="true"
            :readOnlyMode="props.readOnlyMode"
            v-model="selectedConfig"
          />
        </v-sheet>
      </v-col>
      <v-col cols="6">
        <v-sheet rounded="lg" elevation="2" height="100%" class="pa-5">
          <NotificationSettings
            :globalConfig="props.globalConfig"
            :subConfig="true"
            :readOnlyMode="props.readOnlyMode"
            v-model="selectedConfig"
          />
        </v-sheet>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="6">
        <v-sheet rounded="lg" elevation="2" height="100%" class="pa-5">
          <v-row>
            <v-col cols="6">
              <div class="font-weight-bold">
                Inherit Applications from Global Policy
              </div>
              <div class="text-body-2 font-weight-light text-medium-emphasis">
                Enable to inherit applications from the global policy.
                Applications can be added or excluded from this configuration as
                required.
              </div>
            </v-col>
            <v-spacer></v-spacer>
            <v-col cols="auto">
              <v-switch
                :label="selectedConfig.inheritApps ? 'On' : 'Off'"
                color="primary"
                baseColor="grey-darken-2"
                v-model="selectedConfig.inheritApps"
                :readonly="props.readOnlyMode"
              ></v-switch>
            </v-col>
          </v-row>
        </v-sheet>
      </v-col>
    </v-row>
    <v-row v-if="selectedConfig.inheritApps">
      <v-col cols="12">
        <v-sheet rounded="lg" elevation="2" height="100%" class="pa-5">
          <v-tabs color="#e7541e" v-model="selectedAppsTab">
            <v-tab value="add">Additional Apps</v-tab>
            <v-tab value="exclude">Exclude Apps</v-tab>
          </v-tabs>
          <v-window v-model="selectedAppsTab">
            <v-window-item value="add">
              <AppsList
                title="Additional Apps to Include in this configuration"
                :apps="apps"
                v-model="selectedConfig.AppsToAdd"
                :readOnlyMode="props.readOnlyMode"
              ></AppsList>
            </v-window-item>
            <v-window-item value="exclude">
              <AppsList
                title="Apps to Exclude from this configuration"
                :apps="props.apps"
                v-model="selectedConfig.AppsToExclude"
                :readOnlyMode="props.readOnlyMode"
              ></AppsList>
            </v-window-item>
          </v-window>
        </v-sheet>
      </v-col>
    </v-row>
    <v-row v-if="!selectedConfig.inheritApps">
      <v-col cols="12">
        <v-sheet rounded="lg" elevation="2" height="100%" class="pa-5">
          <AppsList
            title="Apps to Patch"
            :apps="apps"
            v-model="selectedConfig.AppsSelected"
            :readOnlyMode="props.readOnlyMode"
          ></AppsList>
        </v-sheet>
      </v-col>
    </v-row>
    <v-row class="mb-5">
      <v-col cols="12">
        <v-sheet rounded="lg" elevation="2" height="100%" class="pa-5">
          <AppRules
            :apps="apps"
            v-model:rules="selectedConfig.AppRules"
            v-model:inheritAppRules="selectedConfig.inheritAppRules"
            group
            :readOnlyMode="props.readOnlyMode"
          />
        </v-sheet>
      </v-col>
    </v-row>
  </template>
</template>
